import { daysUntilDue, dueBucketFromDays, startOfToday } from './dueDate';
import { applyInspectionDue } from './inspectionDueDate';

/**
 * Build Due queue rows ?? shared by Dashboard and Today's To-Do popup.
 * Buckets are mutually exclusive: overdue | urgent (0–30 left) | upcoming (31–60).
 */
export function buildDueQueueRows({
  overdueJobs = [],
  urgentJobs = [],
  overdueBuildings = [],
  dueSoonBuildings = [],
  upcomingDue = null,
  today = startOfToday(),
} = {}) {
  const rows = [];
  const seen = new Set();
  /** building_id::ss_code already finished ?? do not re-add as “No inspection”. */
  const closedSs = new Set();

  const ssKey = (buildingId, ssCode) => `${buildingId}::${ssCode || '—'}`;

  const markClosedFromJobs = (list) => {
    (Array.isArray(list) ? list : []).forEach((job) => {
      if (job?.status === 'COMPLETED' || job?.status === 'REJECTED') {
        if (job.building_id != null && job.ss_code) {
          closedSs.add(ssKey(job.building_id, job.ss_code));
        }
      }
    });
  };

  markClosedFromJobs(overdueJobs);
  markClosedFromJobs(urgentJobs);

  const pushJob = (job) => {
    if (job?.status === 'COMPLETED' || job?.status === 'REJECTED') {
      if (job.building_id != null && job.ss_code) {
        closedSs.add(ssKey(job.building_id, job.ss_code));
      }
      return;
    }
    const key = `job-${job.id}`;
    if (seen.has(key)) return;
    const dueJob = applyInspectionDue(job, today);
    const days = daysUntilDue(dueJob.period_due, today);
    const bucket = dueBucketFromDays(days);
    if (!bucket) return;
    seen.add(key);
    if (job.building_id != null && job.ss_code) {
      closedSs.add(ssKey(job.building_id, job.ss_code)); // has open job ?? building row not needed
    }
    rows.push({
      key,
      bucket,
      building_id: job.building_id,
      building_name: job.building_name || `Building #${job.building_id}`,
      building_address: job.building_address || '',
      company_id: job.company_id || null,
      company_name: job.company_name || null,
      ss_code: job.ss_code,
      ss_name: job.ss_name,
      due_date: dueJob.due_date,
      days,
      job_id: job.id,
      status: job.status,
      assigned_worker_id: job.assigned_worker_id,
      assigned_worker_name: job.assigned_worker_name,
      application_approved: job.application_approved,
    });
  };

  const pushBuildingSs = (b, prefix) => {
    const ssList = Array.isArray(b.ss_list)
      ? b.ss_list
      : Array.isArray(b.ss_codes)
        ? b.ss_codes.map((code) => ({ ss_code: code }))
        : [{ ss_code: '—' }];
    ssList.forEach((s, i) => {
      const dueJob = applyInspectionDue({
        ss_anniversary: s.compliance_schedule_anniversary || s.ss_anniversary || null,
        building_anniversary: b.compliance_schedule_anniversary || null,
        inspection_frequency: s.inspection_frequency || b.inspection_frequency || 'ANNUAL',
      }, today);
      const due = dueJob.due_date || s.nextDueDate || s.due_date || null;
      const days = daysUntilDue(dueJob.period_due || due, today);
      const bucket = dueBucketFromDays(days);
      if (!bucket) return;
      const key = `${prefix}-${b.id}-${s.ss_code || i}`;
      if (seen.has(key)) return;
      if (closedSs.has(ssKey(b.id, s.ss_code || '—'))) return;
      const jobKeyHit = rows.some(
        (r) =>
          r.job_id &&
          r.building_id === b.id &&
          r.ss_code === (s.ss_code || '—')
      );
      if (jobKeyHit) return;
      seen.add(key);
      rows.push({
        key,
        bucket,
        building_id: b.id,
        building_name: b.name,
        building_address: b.address || '',
        company_id: b.company_id || null,
        company_name: b.company_name || null,
        ss_code: s.ss_code || '—',
        ss_name: s.ss_name,
        due_date: due,
        days,
        job_id: null,
        application_approved: b.application_approved,
        default_assignee_name: s.default_assignee_name || null,
        default_assignee_type: s.default_assignee_type || null,
        default_worker_id: s.default_worker_id || null,
        inspection_frequency: s.inspection_frequency || null,
      });
    });
  };

  (Array.isArray(overdueJobs) ? overdueJobs : []).forEach(pushJob);
  (Array.isArray(urgentJobs) ? urgentJobs : []).forEach(pushJob);
  (Array.isArray(overdueBuildings) ? overdueBuildings : []).forEach((b) =>
    pushBuildingSs(b, 'odb')
  );

  (Array.isArray(dueSoonBuildings) ? dueSoonBuildings : [])
    .filter((b) => {
      const days = daysUntilDue(b.due_date, today);
      return days != null && days >= 0 && days <= 30;
    })
    .forEach((b) => pushBuildingSs(b, 'ub'));

  const upcomingList = upcomingDue?.data || (Array.isArray(upcomingDue) ? upcomingDue : []);
  upcomingList.forEach((item, idx) => {
    const days = daysUntilDue(item.due_date, today);
    const bucket = dueBucketFromDays(
      days != null ? days : item.days_until_due != null ? item.days_until_due : null
    );
    if (!bucket) return;
    const key = item.job_id
      ? `job-${item.job_id}`
      : `up-${item.building_id}-${item.ss_code}-${item.due_date}-${idx}`;
    if (seen.has(key)) return;
    if (item.job_id) {
      pushJob({
        id: item.job_id,
        building_id: item.building_id,
        building_name: item.building_name,
        building_address: item.building_address,
        ss_code: item.ss_code,
        ss_name: item.ss_name,
        due_date: item.due_date,
        assigned_worker_id: item.assigned_worker_id,
        assigned_worker_name: item.assigned_worker_name,
        application_approved: item.application_approved,
      });
      return;
    }
    seen.add(key);
    rows.push({
      key,
      bucket,
      building_id: item.building_id,
      building_name: item.building_name || `Building #${item.building_id}`,
      building_address: item.building_address || '',
      ss_code: item.ss_code,
      ss_name: item.ss_name,
      due_date: item.due_date,
      days: days != null ? days : item.days_until_due,
      job_id: null,
      application_approved: item.application_approved,
    });
  });

  rows.sort((a, b) => {
    const da = a.days != null ? a.days : 9999;
    const db = b.days != null ? b.days : 9999;
    return da - db;
  });
  return rows;
}

export function summarizeDueQueue(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const overdueRows = list.filter((r) => r.bucket === 'overdue');
  const urgentRows = list.filter((r) => r.bucket === 'urgent');
  const upcomingRows = list.filter((r) => r.bucket === 'upcoming');
  return {
    overdueRows,
    urgentRows,
    upcomingRows,
    overdueSs: overdueRows.length,
    overdueBuildings: new Set(overdueRows.map((r) => r.building_id).filter(Boolean)).size,
    urgentSs: urgentRows.length,
    urgentBuildings: new Set(urgentRows.map((r) => r.building_id).filter(Boolean)).size,
    upcomingSs: upcomingRows.length,
  };
}
